import { z } from "zod";
import {
  FILE_TYPE_MAPPING,
  BusinessModels,
  countries,
  StageOptions,
  RevenueModels,
} from "@/lib/utils";

// Basic user info schema
export const basicInfoSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  foundedMonth: z.number().min(1).max(12),
  foundedYear: z.number().min(1900).max(new Date().getFullYear()),
});

// Login schema
export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

// Company info schema
export const companyInfoSchema = z.object({
  companyWebsite: z.string().url().optional().or(z.literal("")),
  linkedInUrl: z.string().url().optional().or(z.literal("")),
  address: z.string().min(1, "Address is required"),
  country: z.string().min(1, "Country is required"),
  // GICS hierarchy: Industry (8-digit) → Sub-Industry (10-digit)
  gicsIndustry: z.union([z.string(), z.array(z.string())]),
  gicsSubIndustry: z.union([z.string(), z.array(z.string())]).optional(),
  // Business & Revenue models (multi-select — stored as arrays of short-code values)
  businessModel: z.array(z.string()).min(1, "Business model is required"),
  revenueModel: z.array(z.string()).optional().default([]),
  companyStage: z.string().min(1, "Company stage is required"),
  contactPhone: z.string().optional(),
});

// Document upload schema
export const documentSchema = z.object({
  file: z.any(),
  label: z.string(),
  fileType: z.string(),
  existing: z.boolean(),
  uploaded: z.boolean(),
  _id: z.string().optional(),
  fileUrl: z.string().optional(),
});

// Application form schema
export const applicationSchema = z.record(z.string(), z.string());

// Complete submission schema
export const submissionSchema = z.object({
  basicInfo: basicInfoSchema,
  companyInfo: companyInfoSchema,
  documents: z.array(documentSchema),
  application: applicationSchema,
});

// Individual step schemas for validation
export type BasicInfoFormData = z.infer<typeof basicInfoSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type CompanyInfoFormData = z.infer<typeof companyInfoSchema>;
export type DocumentFormData = z.infer<typeof documentSchema>;
export type ApplicationFormData = z.infer<typeof applicationSchema>;
export type SubmissionFormData = z.infer<typeof submissionSchema>;

// Revenue models list from utils
export const REVENUE_MODEL_LIST = RevenueModels;

// Use the business models from utils (convert to readable format)
export const BUSINESS_MODELS = BusinessModels.map(
  (bm) => `${bm.value} - ${bm.label}`
);

// Use the stage options from utils (provide both label and value)
export const COMPANY_STAGES = StageOptions.map((stage) => ({
  label: stage.label,
  value: stage.value,
}));

// Use the comprehensive countries list from utils
export const COUNTRIES = countries;

// Use common document types for submission form
export const DOCUMENT_LABELS = [
  "Pitch Deck",
  "Company Logo",
  "Company Banner",
  "Financial Report",
  "Founder Bio(s)",
  "Business Model",
  "Market Overview",
  "Product/Service Deck",
  "Competitive Landscape",
  "Business Plan",
  "Other",
];

export const MANDATORY_DOCUMENTS = ["Pitch Deck"];

// Re-export the FILE_TYPE_MAPPING from utils for consistency
export { FILE_TYPE_MAPPING };

export const BUSINESS_TYPE_MAPPING: Record<string, string> = {
  "B2B - Business to Business": "B2B",
  "B2C - Business to Consumer": "B2C",
  "B2B2C - Business to Business to Consumer": "B2B2C",
  "C2C - Consumer to Consumer": "C2C",
  "C2B - Consumer to Business": "C2B",
  "D2C - Direct to Consumer": "D2C",
  "B2P - Business to Platform": "B2P",
  "P2B - Platform to Business": "P2B",
  "P2C - Platform to Consumer": "P2C",
  "C2P - Consumer to Platform": "C2P",
  "C2C2B - Consumer to Consumer to Business": "C2C2B",
  "B2G - Business to Government": "B2G",
  "G2B - Government to Business": "G2B",
  "G2C - Government to Citizen": "G2C",
  "B2E - Business to Employee": "B2E",
  "B2D - Business to Developer": "B2D",
  "D2B - Developer to Business": "D2B",
  "D2D - Developer to Developer": "D2D",
  "M2M - Machine to Machine": "M2M",
  "B2N - Business to Nonprofit": "B2N",
  "C2N - Consumer to Nonprofit": "C2N",
};
